import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PatternRecognition = ({ onPatternSelect }) => {
  const [selectedPattern, setSelectedPattern] = useState(null);

  const candlestickPatterns = [
    {
      id: 'doji',
      name: 'Doji',
      type: 'reversal',
      reliability: 'medium',
      description: 'Indecision pattern with equal open and close prices',
      detected: 3,
      lastSeen: '2 hours ago'
    },
    {
      id: 'hammer',
      name: 'Hammer',
      type: 'bullish',
      reliability: 'high',
      description: 'Bullish reversal pattern with long lower shadow',
      detected: 1,
      lastSeen: '45 minutes ago'
    },
    {
      id: 'shooting_star',
      name: 'Shooting Star',
      type: 'bearish',
      reliability: 'high',
      description: 'Bearish reversal pattern with long upper shadow',
      detected: 2,
      lastSeen: '1 hour ago'
    },
    {
      id: 'engulfing_bull',
      name: 'Bullish Engulfing',
      type: 'bullish',
      reliability: 'high',
      description: 'Strong bullish reversal pattern',
      detected: 1,
      lastSeen: '30 minutes ago'
    },
    {
      id: 'engulfing_bear',
      name: 'Bearish Engulfing',
      type: 'bearish',
      reliability: 'high',
      description: 'Strong bearish reversal pattern',
      detected: 0,
      lastSeen: '6 hours ago'
    },
    {
      id: 'morning_star',
      name: 'Morning Star',
      type: 'bullish',
      reliability: 'very_high',
      description: 'Three-candle bullish reversal pattern',
      detected: 1,
      lastSeen: '1.5 hours ago'
    }
  ];

  const chartPatterns = [
    {
      id: 'head_shoulders',
      name: 'Head & Shoulders',
      type: 'bearish',
      reliability: 'high',
      description: 'Classic bearish reversal pattern',
      detected: 1,
      timeframe: '4H',
      completion: 85
    },
    {
      id: 'double_top',
      name: 'Double Top',
      type: 'bearish',
      reliability: 'medium',
      description: 'Bearish reversal with two peaks',
      detected: 0,
      timeframe: '1D',
      completion: 60
    },
    {
      id: 'triangle_asc',
      name: 'Ascending Triangle',
      type: 'bullish',
      reliability: 'medium',
      description: 'Bullish continuation pattern',
      detected: 2,
      timeframe: '1H',
      completion: 70
    },
    {
      id: 'flag_bull',
      name: 'Bull Flag',
      type: 'bullish',
      reliability: 'high',
      description: 'Bullish continuation pattern',
      detected: 1,
      timeframe: '15M',
      completion: 90
    }
  ];

  const getReliabilityColor = (reliability) => {
    switch (reliability) {
      case 'very_high': return 'text-success';
      case 'high': return 'text-accent';
      case 'medium': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'bullish': return 'text-success';
      case 'bearish': return 'text-error';
      case 'reversal': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bullish': return 'TrendingUp';
      case 'bearish': return 'TrendingDown';
      case 'reversal': return 'RotateCw';
      default: return 'Minus';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Eye" size={20} />
          <span>Pattern Recognition</span>
        </h3>
        <Button variant="ghost" size="sm">
          <Icon name="Settings" size={16} />
        </Button>
      </div>
      <div className="p-4 space-y-6">
        {/* Candlestick Patterns */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Candle" size={16} />
            <span>Candlestick Patterns</span>
          </h4>
          <div className="space-y-2">
            {candlestickPatterns?.map((pattern) => (
              <div
                key={pattern?.id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedPattern === pattern?.id
                    ? 'border-primary bg-primary/10' :'border-border hover:border-muted-foreground'
                }`}
                onClick={() => {
                  setSelectedPattern(pattern?.id);
                  onPatternSelect(pattern);
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Icon name={getTypeIcon(pattern?.type)} size={16} className={getTypeColor(pattern?.type)} />
                    <span className="text-sm font-medium text-foreground">{pattern?.name}</span>
                    <span className={`text-xs px-2 py-1 rounded-full bg-muted ${getReliabilityColor(pattern?.reliability)}`}>
                      {pattern?.reliability?.replace('_', ' ')}
                    </span>
                  </div>
                  {pattern?.detected > 0 && (
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span className="text-xs text-success font-medium">{pattern?.detected}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-1">{pattern?.description}</p>
                <p className="text-xs text-muted-foreground">Last seen: {pattern?.lastSeen}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Chart Patterns */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} />
            <span>Chart Patterns</span>
          </h4>
          <div className="space-y-2">
            {chartPatterns?.map((pattern) => (
              <div
                key={pattern?.id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedPattern === pattern?.id
                    ? 'border-primary bg-primary/10' :'border-border hover:border-muted-foreground'
                }`}
                onClick={() => {
                  setSelectedPattern(pattern?.id);
                  onPatternSelect(pattern);
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Icon name={getTypeIcon(pattern?.type)} size={16} className={getTypeColor(pattern?.type)} />
                    <span className="text-sm font-medium text-foreground">{pattern?.name}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                      {pattern?.timeframe}
                    </span>
                  </div>
                  {pattern?.detected > 0 && (
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span className="text-xs text-success font-medium">{pattern?.detected}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-2">{pattern?.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Completion:</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-1 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-300"
                        style={{ width: `${pattern?.completion}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-foreground">{pattern?.completion}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pattern Alerts */}
        <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Bell" size={16} className="text-accent" />
            <span className="text-sm font-medium text-foreground">Pattern Alerts</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Get notified when new patterns are detected or existing patterns reach completion thresholds.
          </p>
          <Button variant="outline" size="sm" className="w-full">
            <Icon name="Settings" size={14} />
            <span className="ml-1">Configure Alerts</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PatternRecognition;